import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import TopNav from "@/components/TopNav";
import { api } from "@/lib/api";
import { FRIENDS } from "@/constants/testIds";
import { toast } from "sonner";
import { useGlobalSocket } from "@/context/GlobalSocket";

const authHeader = () => {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function Friends() {
  const nav = useNavigate();
  const { refreshRequestCount } = useGlobalSocket();
  const [tab, setTab] = useState("friends");
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadFriends = useCallback(async () => {
    try {
      const r = await api.get("/friends", { headers: authHeader() });
      setFriends(r.data);
    } catch (e) {}
  }, []);
  const loadRequests = useCallback(async () => {
    try {
      const r = await api.get("/friends/requests", { headers: authHeader() });
      setRequests(r.data);
      refreshRequestCount();
    } catch (e) {}
  }, [refreshRequestCount]);

  const reload = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadFriends(), loadRequests()]);
    setLoading(false);
  }, [loadFriends, loadRequests]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const iv = setInterval(loadFriends, 10000);
    return () => clearInterval(iv);
  }, [loadFriends]);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.length < 2) { setResults([]); return; }
      try {
        const r = await api.get(`/users/search`, { params: { q }, headers: authHeader() });
        setResults(r.data);
      } catch (e) {}
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const sendRequest = async (uid) => {
    try {
      const r = await api.post(`/friends/request/${uid}`, {}, { headers: authHeader() });
      const status = r.data?.status;
      if (status === "accepted") toast.success("You're now friends");
      else if (status === "already_friends") toast("Already friends");
      else toast.success("Friend request sent");
      setQ("");
      setResults([]);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
    }
  };
  const accept = async (uid) => {
    try {
      await api.post(`/friends/accept/${uid}`, {}, { headers: authHeader() });
      toast.success("Friend added");
      reload();
    } catch (e) { toast.error("Could not accept"); }
  };
  const decline = async (uid) => {
    try {
      await api.post(`/friends/decline/${uid}`, {}, { headers: authHeader() });
      toast("Request declined");
      reload();
    } catch (e) {}
  };
  const cancel = async (uid) => {
    try {
      await api.post(`/friends/cancel/${uid}`, {}, { headers: authHeader() });
      toast("Request cancelled");
      reload();
    } catch (e) {}
  };
  const remove = async (uid) => {
    if (!window.confirm("Remove this friend?")) return;
    try {
      await api.delete(`/friends/${uid}`, { headers: authHeader() });
      toast("Removed");
      reload();
    } catch (e) {}
  };
  const joinFriend = (room_id) => nav(`/game/${room_id}`);

  const incomingCount = requests.incoming.length;
  const outgoingCount = requests.outgoing.length;
  const joinableCount = friends.filter(f => f.active_room && !f.active_room.is_full && f.active_room.status === "waiting").length;

  const TabButton = ({ id, label, count, tone }) => (
    <button
      data-testid={`${FRIENDS.tabPrefix}${id}`}
      onClick={() => setTab(id)}
      className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition ${
        tab === id
          ? "bg-white text-black shadow-lg"
          : "bg-white/5 text-white/70 hover:bg-white/10"
      }`}
    >
      {label}
      {count > 0 && (
        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
          tab === id ? "bg-black text-white" : (tone === "red" ? "bg-[color:var(--uno-red)] text-white" : "bg-[color:var(--uno-yellow)] text-black")
        }`}>{count}</span>
      )}
    </button>
  );

  return (
    <div className="min-h-screen" data-testid={FRIENDS.container}>
      <TopNav/>
      <main className="max-w-4xl mx-auto px-6 sm:px-10 py-10 relative z-10">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-2">Friends</div>
          <h1 className="font-display text-4xl tracking-tighter">Your rivals.</h1>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <TabButton id="friends" label="Friends" count={joinableCount} />
          <TabButton id="requests" label="Requests" count={incomingCount} tone="red" />
          <TabButton id="find" label="Find people" />
        </div>

        {/* FRIENDS TAB */}
        {tab === "friends" && (
          <div className="rounded-2xl bg-[#161324] border border-white/5 p-6">
            {loading && <div className="text-white/50">Loading…</div>}
            {!loading && friends.length === 0 && (
              <div className="text-center py-10">
                <div className="text-white/50 mb-4">No friends yet.</div>
                <button
                  onClick={() => setTab("find")}
                  className="px-5 py-2 rounded-full bg-[color:var(--uno-green)] text-black font-semibold hover:brightness-110"
                >Find people</button>
              </div>
            )}
            <div className="space-y-2">
              {friends.map(f => {
                const room = f.active_room;
                const joinable = room && room.status === "waiting" && !room.is_full;
                return (
                  <div key={f.user_id} className={`flex items-center justify-between rounded-xl px-4 py-3 border ${joinable ? "bg-[color:var(--uno-green)]/10 border-[color:var(--uno-green)]/40" : "bg-white/5 border-transparent"}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="relative shrink-0">
                        <img src={f.avatar || f.picture} alt="" className="w-11 h-11 rounded-full border border-white/10"/>
                        <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#161324] ${f.is_online ? "bg-green-400":"bg-gray-500"}`}/>
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{f.name}</div>
                        {room ? (
                          <div className="text-xs text-white/70 mt-0.5">
                            <span className="inline-block px-1.5 py-0.5 rounded bg-white/10 text-white/90 uppercase tracking-widest text-[10px] font-semibold mr-1">{room.mode_label || room.mode}</span>
                            {room.status === "waiting"
                              ? (room.is_full ? "Lobby full" : `Waiting · ${room.player_count}/${room.max_players}`)
                              : "Playing…"}
                          </div>
                        ) : (
                          <div className="text-xs text-white/50 mt-0.5">
                            {f.is_online ? "Online" : "Offline"} · {f.wins || 0} wins · {f.score || 0} pts
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {joinable && (
                        <button
                          data-testid={`${FRIENDS.joinBtnPrefix}${f.user_id}`}
                          onClick={() => joinFriend(room.room_id)}
                          className="px-4 py-1.5 rounded-full bg-[color:var(--uno-green)] text-black text-sm font-bold hover:brightness-110"
                        >Join</button>
                      )}
                      <button
                        data-testid={`${FRIENDS.removeBtnPrefix}${f.user_id}`}
                        onClick={() => remove(f.user_id)}
                        className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/15 text-white/70 hover:text-white text-lg"
                        title="Remove"
                      >×</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* REQUESTS TAB */}
        {tab === "requests" && (
          <div className="space-y-4">
            <div className="rounded-2xl bg-[#161324] border border-white/5 p-6">
              <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4">Incoming</div>
              {requests.incoming.length === 0 && <div className="text-white/50 text-sm">Nothing yet.</div>}
              <div className="space-y-2">
                {requests.incoming.map(r => (
                  <div key={r.from.user_id} className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <img src={r.from.avatar} alt="" className="w-10 h-10 rounded-full border border-white/10"/>
                      <div className="font-semibold truncate">{r.from.name}</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        data-testid={`${FRIENDS.acceptBtnPrefix}${r.from.user_id}`}
                        onClick={() => accept(r.from.user_id)}
                        className="px-4 py-1.5 rounded-full bg-[color:var(--uno-green)] text-black text-sm font-bold hover:brightness-110"
                      >Accept</button>
                      <button
                        data-testid={`${FRIENDS.declineBtnPrefix}${r.from.user_id}`}
                        onClick={() => decline(r.from.user_id)}
                        className="px-4 py-1.5 rounded-full bg-white/5 text-white/70 hover:bg-white/15 text-sm"
                      >Decline</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl bg-[#161324] border border-white/5 p-6">
              <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4">Sent</div>
              {requests.outgoing.length === 0 && <div className="text-white/50 text-sm">You haven't sent any requests.</div>}
              <div className="space-y-2">
                {requests.outgoing.map(r => (
                  <div key={r.to.user_id} className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <img src={r.to.avatar} alt="" className="w-10 h-10 rounded-full border border-white/10"/>
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{r.to.name}</div>
                        <div className="text-xs text-white/40">Pending…</div>
                      </div>
                    </div>
                    <button
                      data-testid={`${FRIENDS.cancelBtnPrefix}${r.to.user_id}`}
                      onClick={() => cancel(r.to.user_id)}
                      className="px-4 py-1.5 rounded-full bg-white/5 hover:bg-white/15 text-white/70 text-sm"
                    >Cancel</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* FIND TAB */}
        {tab === "find" && (
          <div className="rounded-2xl bg-[#161324] border border-white/5 p-6">
            <input
              data-testid={FRIENDS.searchInput}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[color:var(--uno-yellow)] mb-4"
              autoFocus
            />
            {q.length > 0 && q.length < 2 && <div className="text-white/40 text-sm">Type at least 2 characters…</div>}
            {q.length >= 2 && results.length === 0 && <div className="text-white/40 text-sm">No matches.</div>}
            <div className="space-y-2">
              {results.map(u => {
                const isFriend = friends.some(f => f.user_id === u.user_id);
                const requested = requests.outgoing.some(r => r.to.user_id === u.user_id);
                const incoming = requests.incoming.some(r => r.from.user_id === u.user_id);
                return (
                  <div key={u.user_id} className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <img src={u.avatar || u.picture} alt="" className="w-10 h-10 rounded-full border border-white/10"/>
                      <div className="font-semibold truncate">{u.name}</div>
                    </div>
                    {isFriend ? (
                      <span className="text-xs text-white/50 uppercase tracking-widest">Friends</span>
                    ) : incoming ? (
                      <button
                        onClick={() => accept(u.user_id)}
                        className="px-4 py-1.5 rounded-full bg-[color:var(--uno-green)] text-black text-sm font-bold"
                      >Accept</button>
                    ) : requested ? (
                      <span className="text-xs text-white/50 uppercase tracking-widest">Requested</span>
                    ) : (
                      <button
                        data-testid={`${FRIENDS.addBtnPrefix}${u.user_id}`}
                        onClick={() => sendRequest(u.user_id)}
                        className="px-4 py-1.5 rounded-full bg-[color:var(--uno-green)] text-black text-sm font-semibold hover:brightness-110"
                      >Add friend</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
